import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "react-feather";
import { useT } from "@reciperoll/core";

// Wraps a horizontal scroll row (scrollbar hidden via no-scrollbar) and shows a
// chevron at each end to signal there's more to scroll. Each chevron hides once
// that end is reached, and clicking one scrolls roughly a page. Adapts to any
// row height (centres vertically) and background (the chevron is a floating
// white pill), so it works for the recipe rails, categories, and cook cards.
export function ScrollRail({
  children,
  className = "",
  chevronTopClass = "top-1/2",
}: {
  children: React.ReactNode;
  className?: string;
  // Vertical anchor for the chevrons (paired with -translate-y-1/2). Defaults to
  // the row's middle; pass e.g. "top-[88px]" to centre on a fixed image height
  // when the row's items also have text below them.
  chevronTopClass?: string;
}) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 1);
    // -1 tolerance for sub-pixel rounding at the far end.
    setCanRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth - 1);
  }, []);

  // Watch container size (window/layout changes) for the mounted lifetime.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [update]);

  // Recompute after every render so it also reacts to content (children) changes.
  useEffect(() => {
    update();
  });

  const nudge = (dir: 1 | -1) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" });
  };

  const btn =
    `hidden sm:flex absolute ${chevronTopClass} -translate-y-1/2 z-[1] w-9 h-9 items-center justify-center rounded-full bg-surface shadow-md ring-1 ring-line text-ink-soft hover:text-ink transition-colors cursor-pointer`;

  return (
    <div className="relative">
      <div ref={ref} onScroll={update} className={`overflow-x-auto no-scrollbar ${className}`}>
        {children}
      </div>

      {canLeft && (
        <button type="button" aria-label={t("common.scrollLeft")} onClick={() => nudge(-1)} className={`${btn} left-1`}>
          <ChevronLeft className="w-5 h-5" />
        </button>
      )}
      {canRight && (
        <button type="button" aria-label={t("common.scrollRight")} onClick={() => nudge(1)} className={`${btn} right-1`}>
          <ChevronRight className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
